import { Button, Sparkline, StatStrip, StatTile, StateBar, formatInt, formatRelative } from '@orgus/ui';
import type { ReactNode } from 'react';
import type { MissionsGlance as Glance } from './glance.ts';

/** "1 item", "74 items". */
const count = (n: number, noun: string): string => `${formatInt(n)} ${noun}${n === 1 ? '' : 's'}`;

/**
 * A caption that never truncates on a phone: the first fact always, the rest from 640 px up. The
 * whole sentence is the tile's tooltip. With `phone`, below 640 px the caption is those short facts
 * instead, so it still adds up to the tile's number (QA-36).
 */
function Facts({ facts, phone }: { facts: string[]; phone?: string[] }): ReactNode {
  const [first, ...rest] = facts;
  if (phone) {
    return (
      <>
        <span className="sm:hidden">{phone.join(', ')}</span>
        <span className="max-sm:hidden">{facts.join(', ')}</span>
      </>
    );
  }
  return (
    <>
      {first}
      {rest.length > 0 && <span className="max-sm:hidden">, {rest.join(', ')}</span>}
    </>
  );
}

/**
 * The Missions glance strip: what waits on you (with the way to answer it, and items a person
 * reviews), what is under way, what is In review (items and AI proposals, as the Review tab counts them), and how many items became Done this week. Each tile opens the list
 * behind it. A figure (bar, sparkline) shows only when it has something to draw, and a tile at
 * zero reads as quiet text: an empty bar or a bright 0 reads as broken, not as calm.
 */
export function MissionsGlance({
  g,
  now,
  outsideInbox = 0,
  onAnswer,
  onMissions,
  onReview,
  onFinished,
}: {
  g: Glance;
  now: number;
  /** Waiting missions the Inbox does not list (old, set aside or answered by Orgus): answered on their page. */
  outsideInbox?: number;
  /** Open the mission that has waited longest, where its question is answered. */
  onAnswer: () => void;
  onMissions: () => void;
  onReview: () => void;
  onFinished: () => void;
}) {
  const answer = g.waiting - g.blocked;
  // Every open mission, as the Missions tab counts them: one that waits on you is still under way (R-FN-8).
  const underway = g.running + g.planned + g.waiting;
  // ORGU-85: items a person reviews wait on you too; those Orgus reviews do not.
  const needs = g.waiting + g.reviewByPeople;
  const byOrgus = g.review - g.reviewByPeople;
  const needFacts = [answer && `${formatInt(answer)} to answer`, outsideInbox > 0 && `${formatInt(outsideInbox)} on the mission page, not in the Inbox`, g.blocked && `${formatInt(g.blocked)} blocked`, g.reviewByPeople && `${formatInt(g.reviewByPeople)} to review`].filter((f): f is string => !!f);
  const needSum = [answer && `${formatInt(answer)} to answer`, g.blocked && `${formatInt(g.blocked)} blocked`, g.reviewByPeople && `${formatInt(g.reviewByPeople)} to review`].filter((f): f is string => !!f);
  const oldest = g.review && byOrgus !== g.review ? formatRelative(g.oldestReviewAt ?? now, now) : null;
  // Everything the Review tab lists: finished items and open AI proposals, the same number as its tab count.
  const inReview = g.review + g.proposals;
  const reviewFacts = [g.review && count(g.review, 'item'), g.proposals && count(g.proposals, 'proposal')].filter((f): f is string => !!f);
  return (
    <StatStrip label="Mission counts" className="lg:grid-cols-4">
      <StatTile
        label="Needs you"
        value={needs}
        attention={needs > 0}
        quietAtZero
        // On a phone only the parts that add up to the number: "not in the Inbox" is a part of "to answer".
        sub={needs ? <Facts facts={needFacts} phone={needSum} /> : 'Nothing needs you'}
        hint={needFacts.length > 1 ? needFacts.join(', ') : undefined}
        onOpen={g.waiting ? onMissions : onReview}
        action={
          g.firstWaitingId ? (
            <Button variant="secondary" size="sm" onClick={onAnswer}>
              {answer ? 'Answer' : 'Unblock'}
            </Button>
          ) : g.reviewByPeople ? (
            <Button variant="secondary" size="sm" onClick={onReview}>
              Review
            </Button>
          ) : undefined
        }
      />
      <StatTile
        label="Under way"
        value={underway}
        quietAtZero
        sub={[g.running && `${formatInt(g.running)} running`, g.waiting && `${formatInt(g.waiting)} ${g.waiting === 1 ? 'needs' : 'need'} you`, g.planned && `${formatInt(g.planned)} queued`].filter(Boolean).join(', ') || 'Nothing under way'}
        visual={g.running ? <StateBar counts={{ running: g.running, 'needs-you': g.waiting, queued: g.planned }} label={`${formatInt(g.running)} running, ${formatInt(g.waiting)} need you, ${formatInt(g.planned)} queued`} /> : undefined}
        onOpen={onMissions}
      />
      <StatTile
        label="In review"
        value={inReview}
        quietAtZero
        sub={
          !inReview ? (
            'Nothing to review'
          ) : g.proposals ? (
            <Facts facts={reviewFacts} />
          ) : oldest ? (
            <>
              Oldest <span className="max-sm:hidden">finished </span>
              {oldest}
            </>
          ) : (
            'Orgus is reviewing them'
          )
        }
        hint={reviewFacts.length > 1 ? reviewFacts.join(', ') : undefined}
        onOpen={onReview}
      />
      <StatTile
        label="Done this week"
        featured
        value={g.finishedThisWeek}
        quietAtZero
        // The all-time count only when it says more than the week's: "5 done this week, 5 done in all" repeats itself.
        sub={g.doneItems > g.finishedThisWeek ? `${formatInt(g.doneItems)} done in all` : !g.finishedThisWeek ? 'None done yet' : undefined}
        trend={g.finishedByDay.some((n) => n > 0) ? <Sparkline values={g.finishedByDay} width={88} height={32} min={0} slot={3} label={`Items done per day, last ${formatInt(g.finishedByDay.length)} days`} /> : undefined}
        onOpen={onFinished}
      />
    </StatStrip>
  );
}
